import { ComponentPropsWithRef, useEffect, useId, useState } from "react"
import { CustomComponents } from "react-day-picker"
import { CalendarBlank } from "@phosphor-icons/react"
import { format, parse } from "date-fns"
import { t } from "i18next"
import { Calendar, CalendarFooter } from "@/components/ui/calendar"
import { FormError } from "@/components/ui/form"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDate } from "@/lib/format-date"
import { cn } from "@/lib/utils"

export const VISUAL_DATE_PLACEHOLDER = "DD.MM.YYYY"
export const VISUAL_DATE_FORMAT = "dd.MM.yyyy"
export const VISUAL_DATE_SEPARATOR = "."

const separatorRegex = /\./
const dayRegex = /(0[1-9]|[1-2][0-9]|3[0-1])/ // day between 1 and 9 with a leading zero OR from 10 to 31
const monthRegex = /(0[1-9]|1[0-2])/ // month between 1 and 9 with a leading zero OR from 10 to 12
const yearRegex = /(19[0-9][0-9]|2[0-1][0-9][0-9])/ // year between 1900 and 2199
export const validDateRegex = new RegExp(
  "^" + dayRegex.source + separatorRegex.source + monthRegex.source + separatorRegex.source + yearRegex.source + "$",
)
// this regex is similar to the above one but allows partial dates as we type, e.g.
// d
// dd
// dd.
// dd.M
// dd.MM
// dd.MM.
// dd.MM.y
// dd.MM.yy
// dd.MM.yyy
// dd.MM.yyyy
const dayWhileTypingRegex = /([0-3]|0[1-9]|[1-2][0-9]|3[0-1])/ // day between 1 and 9 with a leading zero OR from 10 to 31
const monthWhileTypingRegex = /([0-1]|0[1-9]|1[0-2])/ // month between 1 and 9 with a leading zero OR from 10 to 12
const yearWhileTypingRegex = /([1-2]|19|2[0-1]|19[0-9]|2[0-1][0-9]|19[0-9][0-9]|2[0-1][0-9][0-9])/ // year between 1900 and 2199
export const validDateWhileTypingRegex = new RegExp(
  "^(" +
    `${dayWhileTypingRegex.source}|` +
    `${dayRegex.source}${separatorRegex.source}${monthWhileTypingRegex.source}?|` +
    `${dayRegex.source}${separatorRegex.source}${monthRegex.source}${separatorRegex.source}?|` +
    `${dayRegex.source}${separatorRegex.source}${monthRegex.source}${separatorRegex.source}${yearWhileTypingRegex.source}` +
    ")$",
)

const validateDateFormat = (input: string) => {
  return validDateRegex.test(input)
}

interface DatePickerProps extends Omit<DateInputProps, "onSelect" | "value"> {
  components?: CustomComponents
  disabledMatcher?: (date: Date) => boolean
  onSelect?: (date: Date) => void
  onReset?(): void
  enableReset?: boolean
  value?: Date | undefined
  today?: Date
  translateError?: boolean
  error?: string
  inputRef?: React.RefObject<HTMLInputElement | null>
  calendarIconRef?: React.RefObject<HTMLButtonElement | null>
  wrapperClassName?: string
}

interface DateInputProps extends ComponentPropsWithRef<"input"> {
  label?: string
  dataTestId?: string
  focus?: boolean
  error?: string
  readOnly?: boolean
  errorClasses?: React.HTMLAttributes<HTMLParagraphElement>["className"]
  translateError?: boolean
  onCalendarIconClick?: React.MouseEventHandler<HTMLButtonElement>
  inputRef?: React.RefObject<HTMLInputElement | null>
  calendarIconRef?: React.RefObject<HTMLButtonElement | null>
  wrapperClassName?: string
}

export const DateInput = ({
  className,
  focus,
  value,
  label,
  dataTestId,
  children,
  readOnly = true,
  error,
  errorClasses,
  translateError,
  required,
  placeholder,
  onCalendarIconClick,
  inputRef,
  calendarIconRef,
  wrapperClassName,
  disabled,
  ref,
  ...inputProps
}: DateInputProps) => {
  const datePickerId = useId()

  return (
    <>
      <div className={cn("relative", wrapperClassName)} data-testid={dataTestId}>
        <div ref={ref}>
          <input
            ref={inputRef}
            id={datePickerId}
            className={cn(
              "border-border bg-surface-subdued text-on-surface hover:bg-surface-subdued-hovered focus:outline-primary focus-visible:outline-primary h-12 w-full justify-start rounded-md border pr-1 pl-3 text-left text-sm font-normal transition-colors duration-300 ease-in-out focus:outline-offset-0 focus-visible:outline-offset-0",
              focus && !error ? "border-primary ring-primary border-2" : "border-border border",
              label && "pt-4.75",
              error && "outline-critical! outline-1 outline-offset-0 focus:outline-2",
              disabled && "pointer-events-none cursor-not-allowed border-none",
              className,
            )}
            {...inputProps}
            value={value}
            readOnly={readOnly}
            placeholder={placeholder}
          />
          <button
            ref={calendarIconRef}
            onClick={onCalendarIconClick}
            className={cn(
              "text-on-surface-subdued hover:bg-surface-hovered absolute top-1 right-2 flex h-10 w-10 items-center justify-center rounded-full duration-200",
              disabled && "text-on-surface-disabled pointer-events-none cursor-not-allowed",
            )}
            tabIndex={-1}>
            <CalendarBlank />
          </button>
        </div>
        <label
          htmlFor={datePickerId}
          data-testid="date-input-label"
          className={cn(
            "text-on-surface-subdued pointer-events-none absolute inset-y-3.5 left-3.5 h-fit text-sm leading-4! transition-all duration-300 ease-in-out",
            (focus || value || placeholder) && "top-1.5 bg-transparent text-xs",
            focus && "text-primary",
            error && "text-critical",
            error && !value && !focus && !placeholder && "text-on-surface-subdued",
            disabled && "text-on-surface-disabled",
          )}>
          {label}
          {required && "*"}
        </label>
        {error && <FormError className={errorClasses}>{translateError ? t(error) : error}</FormError>}
      </div>
      {children}
    </>
  )
}

export const DatePickerFooter = (props: { children: React.ReactNode }) => {
  return <div>{props.children}</div>
}

export const DatePicker = (props: DatePickerProps) => {
  const {
    onSelect,
    onReset,
    value,
    children,
    components,
    today = new Date(),
    disabledMatcher,
    translateError,
    error,
    inputRef,
    calendarIconRef,
    enableReset = true,
    ...dateInputProps
  } = props
  const [isFocused, setIsFocused] = useState(false)
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState<Date | null>(value ?? null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ?? null)
  const [fieldValue, setFieldValue] = useState<string | null>(formatDate(value, VISUAL_DATE_FORMAT))
  const [componentError, setComponentError] = useState<string | null>(null)

  useEffect(() => {
    const formattedValue = value === undefined ? null : format(value, VISUAL_DATE_FORMAT)
    const getDateReference = (stateDate: Date | null) => {
      // smart comparison so we don't set a new instance of a Date when not necessary
      // if former date has the same value as the new one, then we reassign former date to the state
      // this way the instance reference stays the same and it won't rerender
      if (stateDate === value) {
        return stateDate
      }

      if (stateDate === null || value === undefined) {
        return value ?? null
      }

      if (format(stateDate, VISUAL_DATE_FORMAT) === formattedValue) {
        return stateDate
      }

      return value ?? null
    }

    setSelectedDate(getDateReference)
    setMonth(getDateReference)
    setFieldValue(formattedValue)
    setComponentError(null)
  }, [value])

  const handleSelect = (date: Date | undefined, closeDatePicker: boolean | undefined) => {
    if (formatDate(selectedDate ?? undefined) === formatDate(date)) {
      return
    }

    if (!date) {
      onReset?.()
    } else {
      onSelect?.(date)
    }

    setSelectedDate(date ?? null)
    setFieldValue(formatDate(date, VISUAL_DATE_FORMAT))
    setComponentError(null)
    if (closeDatePicker) {
      setOpen(false)
      setIsFocused(false)
    }
  }

  const handleReset = () => {
    setSelectedDate(null)
    setFieldValue("")
    setComponentError(null)
    onReset?.()
  }

  const handleOpenChange = (state: boolean) => {
    setIsFocused(state)
    setOpen(state)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const val = fieldValue
      if (val && (val.length === 3 || val.length === 6)) {
        setFieldValue(val.slice(0, val.length - 1))
      }
      return
    }
  }

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    let newText = evt.target.value

    if (newText && !validDateWhileTypingRegex.test(newText)) {
      evt.preventDefault()
      return
    }

    if (newText.length === 2 || newText.length === 5) {
      newText += VISUAL_DATE_SEPARATOR
    }

    setFieldValue(newText)
    validateDate(newText)
  }

  const handleInputClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setOpen(true)
    setIsFocused(true)
  }

  const handleCalendarIconClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setOpen((open) => !open)
    setIsFocused(true)
  }

  const validateDate = (input: string) => {
    setIsFocused(input !== "")
    if (!validateDateFormat(input)) {
      setSelectedDate(null)
      setComponentError("common.fieldErrorList.invalidDateRange")
      return
    }

    const parsedDate = parse(input ?? "", VISUAL_DATE_FORMAT, new Date())

    // If parsing failed, Invalid Date will be returned. Invalid Date is a Date, whose time value is NaN.
    // See https://date-fns.org/v3.1.0/docs/parse
    if (Number.isNaN(parsedDate.getTime())) {
      setComponentError("common.fieldErrorList.invalidDateRange")
      return
    }

    if (disabledMatcher && disabledMatcher(parsedDate)) {
      setSelectedDate(null)
      setMonth(parsedDate)
      setComponentError("common.fieldErrorList.invalidDateRange")
      return
    }

    if (formatDate(selectedDate) === formatDate(parsedDate)) {
      setComponentError(null)
      return
    }

    setComponentError(null)
    setSelectedDate(parsedDate)
    setMonth(parsedDate)
    onSelect?.(parsedDate)
  }

  const customComponents = enableReset
    ? { ...components, Footer: () => <CalendarFooter disabled={!selectedDate} onClick={handleReset} /> }
    : components

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <DateInput
          {...dateInputProps}
          value={fieldValue ?? ""}
          focus={isFocused}
          type="text"
          onClick={handleInputClick}
          onCalendarIconClick={handleCalendarIconClick}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          error={componentError ?? error ?? undefined}
          translateError={translateError}
          inputRef={inputRef}
          calendarIconRef={calendarIconRef}>
          {children}
        </DateInput>
      </PopoverTrigger>

      <PopoverContent
        sideOffset={0}
        className="bg-surface w-auto rounded-lg p-0"
        align="start"
        autoFocus={false}
        onOpenAutoFocus={(e) => {
          // prevent auto focus on PopoverContent, instead focus on input
          e.preventDefault()
        }}>
        <Calendar
          mode="single"
          onSelect={(date) => {
            handleSelect(date, true)
          }}
          selected={selectedDate ?? undefined}
          month={month ?? undefined}
          onMonthChange={setMonth}
          today={today}
          disabledMatcher={disabledMatcher}
          components={{
            ...customComponents,
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
